import { useState, Dispatch, SetStateAction } from "react";
import { Tooltip, TextField } from "@mui/material";
import PopupHeader from "./PopupHeader";
import PopupFooter from "./PopupFooter";
import { Btn } from "../Buttons/Buttons";
import { ReadAcrossDataType } from "../../routes/ReadAcross/page/IndividualReadAcrossPage";
import StructureComponent from "./DetailsPopupComponents/StructureComponent";
import CopyToClipbardButton from "../Buttons/CopyToClipboardButton";
import StatsValue from "./DetailsPopupComponents/StatsValue";
import ModalBase from "./ModalBase";
import CategoryFilter from "../../routes/ReadAcross/components/HeatmapComponents/CategoryFilter"
import { filterOptions } from "../../routes/ReadAcross/page/IndividualReadAcrossPage";


export default function RADetailsModal({
    data,
    index,
    selectedFilter,
    setSelectedFilter,
    searchInput,
    setSearchInput,
    open: openProp,
    setOpen: setOpenProp,
} : {
    data: ReadAcrossDataType,
    index: number,
    selectedFilter: string,
    setSelectedFilter: Dispatch<SetStateAction<string>>,
    searchInput: string,
    setSearchInput: Dispatch<SetStateAction<string>>,
    open?: boolean,
    setOpen?: Dispatch<SetStateAction<boolean>>,
}) {
    const [internalOpen, setInternalOpen] = useState(false);

    const isControlled = openProp !== undefined && setOpenProp !== undefined;
    const open = isControlled ? openProp : internalOpen;
    const setOpen = isControlled ? setOpenProp : setInternalOpen;
    const handleClose = () => setOpen(false);
    const handleOpen = () => setOpen(true);

    const btns: Btn[] = [
        {
            type: 'primary',
            styling: "solid",
            id: 'close-details-popup',
            label: "Close",
            action: handleClose,
        }
    ];

    // --- Filtering logic for endpoints (toxKeys) ---
    const allToxKeys = index === 0
        ? Object.keys(data.reg_pred ?? {})
        : Array.from(new Set([
            ...Object.keys(data.true_value ?? {}),
            ...Object.keys(data.reg_pred ?? {}),
            ...Object.keys(data.clf_pred ?? {}),
            ...Object.keys(data.toxicology_data ?? {}),
        ]));


    const filteredToxKeys = allToxKeys.filter(toxKey => {
        const matchesSearch = toxKey.toLowerCase().includes(searchInput.toLowerCase());
        if (!matchesSearch) return false;

        if (selectedFilter === "toxic-only") {
            if (index === 0) {
                const clf_pred = data.clf_pred?.[toxKey];
                return clf_pred === 1;
            } else {
                const trueValue = data.true_value?.[toxKey] as number;
                const reg_pred = data.reg_pred?.[toxKey] as number;
                return trueValue > 0 || reg_pred > 0;
            }
        }
        if (selectedFilter === "data-only") {
            if (index === 0) {
                const trueValue = data.true_value?.[toxKey];
                const clf_pred = data.clf_pred?.[toxKey];
                const clf_proba = data.clf_proba?.[toxKey];
                const reg_pred = data.reg_pred?.[toxKey];
                return (
                    (clf_pred !== null && clf_pred !== undefined) ||
                    (clf_proba !== null && clf_proba !== undefined) ||
                    (trueValue !== null && trueValue !== undefined) ||
                    (reg_pred !== null && reg_pred !== undefined)
                );
            } else {
                const trueValue = data.true_value?.[toxKey];
                const reg_pred = data.reg_pred?.[toxKey];
                return (trueValue !== null && trueValue !== undefined) ||
                    (reg_pred !== null && reg_pred !== undefined)
            }
        }
        return true;
    });

    return (
        <div>
            <button onClick={handleOpen} className="absolute top-0 left-0 right-0 bottom-0"></button>
            <ModalBase
                open={open}
                modalId="readacross-details-modal"
            >
                <div className={`bg-white rounded-lg w-[900px] mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch'>
                        <PopupHeader
                            label='Molecule Details'
                            hasOutline={true}
                            closeFn={handleClose}
                        />
                        <div className="w-full flex justify-center items-stretch">
                            <StructureComponent value={data.smiles}/>
                            <div className="grow border-l border-outline w-full max-h-[500px] overflow-y-auto">
                                <div className="w-full flex flex-col p-6 gap-8">
                                    {/* SMILES and Chemical Name */}
                                    <div className="w-full flex flex-col gap-2 justify-start items-start">
                                        <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">
                                            SMILES string
                                            <CopyToClipbardButton copyValue={data.smiles}/>
                                        </label>
                                        <p className={`text-default break-all`}>{data.smiles}</p>
                                    </div>
                                    <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                        <div className="w-full flex flex-col gap-2 justify-start items-start">
                                            <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">Chemical Name</label>
                                            <p className="text-default">{data.chemical_name}</p>
                                        </div>
                                        <div className="w-full flex flex-col gap-2 justify-start items-start">
                                            <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">Tanimoto</label>
                                            <p className="text-default">{data.tanimoto}</p>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="font-semibold text-default my-2">Assay Endpoints</p>
                                        {/* --- Main endpoints section --- */}
                                        {Object.keys(data.toxicology_data).length > 0 ? (
                                            <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                                {Object.keys(data.toxicology_data).map(toxKey => {
                                                    const toxValue = data.toxicology_data[toxKey];
                                                    return (
                                                        <div className="w-full flex flex-col gap-2 justify-start items-start" key={toxKey}>
                                                            <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">{toxKey}</label>
                                                            <StatsValue 
                                                                value={toxValue?.toString()}
                                                                isReversed
                                                            />
                                                        </div>
                                                    )})}
                                            </div>
                                        ) : (index === 0 ? (
                                            <>
                                                {/* --- Search and Hide No Data controls --- */}
                                                <div className="flex flex-row items-left mb-4 py-4 pr-4 gap-2 justify-between border-y">
                                                    <div className="w-full flex flex-col items-stretch pr-2">

                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        label="Search endpoints"
                                                        value={searchInput}
                                                        onChange={e => setSearchInput(e.target.value)}
                                                    />
                                                    </div>
                                                    <CategoryFilter
                                                        options={filterOptions}
                                                        selectedFilter={selectedFilter}
                                                        setSelectedFilter={setSelectedFilter}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap gap-6 w-full">
                                                    {filteredToxKeys.length === 0 && (
                                                        <div className="italic text-gray-400">No endpoints found.</div>
                                                    )}
                                                    {filteredToxKeys.map(toxKey => {
                                                        const trueValue = data.true_value?.[toxKey];
                                                        const confusionLabel = data.confusion_label?.[toxKey];
                                                        const clf_pred = data.clf_pred?.[toxKey];
                                                        const clf_proba = data.clf_proba?.[toxKey];
                                                        const auc = data.auc?.[toxKey];
                                                        const pval = data.pval?.[toxKey];
                                                        // const t0 = data.t0?.[toxKey];

                                                        // Determine classification display and color
                                                        let classificationText = "";
                                                        let classificationBg = "#E0E0E0";
                                                        if (clf_pred === 0) {
                                                            classificationText = "No Effect";
                                                            classificationBg = "#7BC960";
                                                        } else if (clf_pred === 1) {
                                                            classificationText = "Active";
                                                            classificationBg = "#F87171";
                                                        }

                                                        // Border color by confusion label
                                                        let borderColor = "#E0E0E0";
                                                        if (confusionLabel === "TN" || confusionLabel === "FP") borderColor = "#7BC960";
                                                        else if (confusionLabel === "TP" || confusionLabel === "FN") borderColor = "#F87171";

                                                        // Tooltip content for classification
                                                        const classificationTooltip = confusionLabel ? `Confusion Label: ${confusionLabel}` : "";

                                                        // Tooltip content for ACT
                                                        const actTooltip = (
                                                            <div>
                                                                <div><b>AUC:</b> {auc !== null && auc !== undefined ? Math.round(auc * 1000) / 1000 : "N/A"}</div>
                                                                <div><b>p-val:</b> {pval !== null && pval !== undefined ? Math.round(pval * 1000) / 1000 : "N/A"}</div>
                                                                {/* <div><b>t0:</b> {t0 !== null && t0 !== undefined ? Math.round(t0 * 1000) / 1000 : "N/A"}</div> */}
                                                            </div>
                                                        );

                                                        const hasAnyData = (clf_pred !== null && clf_pred !== undefined) || (clf_proba !== null && clf_proba !== undefined) || (trueValue !== null && trueValue !== undefined);

                                                        return (
                                                            <div key={toxKey} className="min-w-[250px] w-full border border-outline rounded-lg p-3 flex flex-col items-stretch">
                                                                <div className="font-semibold mb-2 text-default">{toxKey}</div>
                                                                {hasAnyData ? (
                                                                    <>
                                                                        {/* Classification / ACT Row */}
                                                                        <div className="flex flex-row items-center gap-2 mb-2 justify-between">
                                                                            <span className="text-xs font-medium text-gray-600 min-w-[110px]">Classification / ACT</span>
                                                                            <div className="flex flex-row items-center gap-2 mb-2">
                                                                                {/* Classification Value */}
                                                                                <Tooltip title={classificationTooltip} arrow>
                                                                                    <div
                                                                                        className="px-3 py-1 rounded-lg font-semibold text-sm text-white cursor-pointer border-2"
                                                                                        style={{
                                                                                            backgroundColor: classificationBg,
                                                                                            borderColor: borderColor,
                                                                                        }}
                                                                                    >
                                                                                        {classificationText || "-"}
                                                                                    </div>
                                                                                </Tooltip>
                                                                                {/* ACT Value */}
                                                                                <Tooltip title={actTooltip} arrow>
                                                                                    <div
                                                                                        className="px-3 py-1 rounded-lg font-semibold text-sm bg-[#E0E0E0] text-default cursor-pointer ml-2"
                                                                                    >
                                                                                        {typeof(clf_proba) === "number" ? Math.round(clf_proba * 1000) / 1000 : "-"}
                                                                                    </div>
                                                                                </Tooltip>
                                                                            </div>
                                                                        </div>
                                                                        {/* True Value Row */}
                                                                        {trueValue !== null && trueValue !== undefined && (
                                                                            <div className="flex flex-row items-center gap-2 justify-between">
                                                                                <span className="text-xs font-medium text-gray-600 min-w-[110px]">True Value</span>
                                                                                <div
                                                                                    className="px-3 py-1 rounded-lg font-semibold text-sm text-default text-white"
                                                                                    style={{
                                                                                        backgroundColor: trueValue === 0 ? "#7BC960" : "#F87171",
                                                                                    }}
                                                                                >
                                                                                    {trueValue}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <div className="italic text-gray-400">No data</div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                            <div className="flex flex-row items-left mb-4 py-4 pr-4 gap-2 justify-between border-y">
                                                <div className="w-full flex flex-col items-stretch pr-2">

                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        label="Search endpoints"
                                                        value={searchInput}
                                                        onChange={e => setSearchInput(e.target.value)}
                                                    />
                                                    </div>
                                                    <CategoryFilter
                                                        options={filterOptions}
                                                        selectedFilter={selectedFilter}
                                                        setSelectedFilter={setSelectedFilter}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                                    {filteredToxKeys.map(toxKey => {
                                                        const toxValue = data.reg_pred?.[toxKey] ?? "No Data";
                                                        return (
                                                            <div className="w-full flex flex-col gap-2 justify-start items-start" key={toxKey}>
                                                                <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">{toxKey}</label>
                                                                <div
                                                                    className="px-3 py-1 rounded-lg font-semibold text-sm text-default"
                                                                    style={{
                                                                        backgroundColor: toxValue === "No Data" ? "#E0E0E0" : toxValue === 0 ? "#7BC960" : "#F87171",
                                                                        color: toxValue === "No Data" ? "primary" : "white"
                                                                    }}
                                                                >
                                                                    {toxValue}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </>
                                        ))}
                                    </div>

                                    <p className="font-semibold text-default mt-4">Physiochemical Properties</p>
                                    <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                        {Object.keys(data.physchem_data).map(physKey => {
                                            const physValue = data.physchem_data[physKey];
                                            return (
                                                <div className="w-full flex flex-col gap-2 justify-start items-start" key={physKey}>
                                                    <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">{physKey}</label>
                                                    <p className="text-default">{physValue}</p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                        />
                    </div>
                </div>
            </ModalBase>
        </div>
    )
}